import { Cell, Graph, Node } from "@antv/x6"
import { createEdgeOptionFromRecord, createNodeOptionsFromNodeRecord, createUnitTagFromEdge, createUnitTagFromRecord, getTagIdByNodeId, jumpStateToNodeOptions } from "../nodes"
import { FlowsheetContent, ILineEnd, LineRecord, NodeShapes, SheetContent } from "./modal"
import { LogKinds, log } from "@/common/log"
import { jumpRecordToState, nodeRecordToState } from "./recordToState"
import { isLineValid } from "../utils"

export function resetSheet(content: SheetContent, graph: Graph, readonly = false) {
  const {nodes, lines, connections = [], polylines = [], jumps = []} = content

  const connectionCells = connections.map(item => {
    return graph.createNode(item)
  })

  const polylineCells = polylines.filter(item => isLineValid(item)).map(item => {
    if (item.attrs?.path?.pointerEvents) {
      delete item.attrs?.path?.pointerEvents;
    }
    return graph.createEdge(item)
  })
  const jumpNodes = jumps.map(item => {
    const option = jumpStateToNodeOptions(jumpRecordToState(item))
    return graph.createNode(option)
  })
  const nodeCells = nodes.flatMap(item => {
    const nodeOptions = createNodeOptionsFromNodeRecord(item)
    const svgNode = graph.createNode(nodeOptions)
    if (item.showTag !== false) {
      return [svgNode, graph.createNode(createUnitTagFromRecord(nodeRecordToState(item)))]
    }
    return [svgNode]
  })
  const lineCells = lines.flatMap(item => {
    const edgeOptions = createEdgeOptionFromRecord(item as LineRecord)
    const streamEdge = graph.createEdge(edgeOptions)
    if (item.showTag !== false) {
      const offset = item.tagOption?.offset
      if (offset?.cx !== undefined && offset?.cy !== undefined) {
        const center = {x: offset.cx, y: offset.cy}
        return [streamEdge, graph.createNode(createUnitTagFromEdge(item, center))]
      }
    }
    return [streamEdge]
  })
  const allCells = [...nodeCells, ...connectionCells, ...jumpNodes, ...lineCells, ...polylineCells]

  graph.resetCells(allCells)
  if (readonly) {
    allCells.forEach((cell) => {
      const view = graph.findViewByCell(cell);
      view?.container.setAttribute('pointer-events', 'none')
    })
  }
  console.log('reset finished.......')
}

export function resetTempCells(cells: Cell.Properties[], graph: Graph, isTemplate = false) {
  cells.forEach(item => {
    let cell;
    if (item.shape === NodeShapes.Edge) {
      cell = graph.addEdge(item)
      if (!isTemplate) {
        const view = graph.findViewByCell(cell);
        view?.container.setAttribute('pointer-events', 'none')
      }
    } else {
      cell = graph.addNode(item)
      if (!isTemplate) {
        const view = graph.findViewByCell(cell);
        
        if (![NodeShapes.TextNode, NodeShapes.ImageNode].includes(item.shape as NodeShapes)) {
          view?.container.setAttribute('pointer-events', 'none')
        }
      }
    }
    
  });
}

export function resetImagesToNodes(content: FlowsheetContent) {
  content.sheets.forEach(sheet => {
    sheet.nodes.forEach(node => {
      node.nozzles.forEach(nozzle => {
        if (nozzle.shapeId) {
          nozzle.image = decodeURIComponent(content.nozzleImages[nozzle.shapeId])
        }
      })
      node.image = decodeURIComponent(content.nodeImages[node.equipment.id])
    })
  })
  return verifyContentAndClean(content)
}

export function verifyContentAndClean(content: FlowsheetContent) {
  const sheets = content.sheets;
  sheets.forEach(sheet => {
    const lines = sheet.lines;
    const nodes = [...sheet.nodes, ...(sheet.connections || []), ...(sheet.jumps || [])];
    const nodeMap = nodes.reduce((acc, cur) => {
      acc[cur.id] = cur;
      return acc;
    }, {})
    sheet.lines = lines.filter((line) => {
      const source = line.source as ILineEnd;
      const target = line.target as ILineEnd;
      const ids = [source.cell, target.cell].filter(item => !!item)
      if (ids.length === 0) return true;
      return ids.every((id) => !!nodeMap[id as string])
    })
    if (lines.length !== sheet.lines.length) {
      log(`[${LogKinds.Flowsheet}]: ${lines.length - sheet.lines.length} streams be cleaned.`)
    }
  })
  return content
}

export function contentIsValid(content: FlowsheetContent) {
  const sheets = content.sheets;
  const inValidSheet = sheets.find(sheet => {
    const lines = sheet.lines;
    const nodes = [
      ...sheet.nodes,
      ...(sheet.connections || []),
      ...(sheet.jumps || []),
    ];
    const nodeMap = nodes.reduce((acc, cur) => {
      acc[cur.id] = cur;
      return acc;
    }, {})
    return lines.find((line) => {
      const source = line.source as ILineEnd;
      const target = line.target as ILineEnd;
      const ids = [source.cell, target.cell].filter(item => !!item)
      if (ids.length === 0) return false;
      return ids.some((id) => !nodeMap[id as string])
    })
  })
  return !inValidSheet
}